import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { FormProvider, useForm } from 'react-hook-form';
import apiConfig from 'constants/apiConfig';
import {
  roleScopeOptions,
  type RoleScopeMember,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/audienceModel';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import FormGeoLocationField from 'components/IAP/formFields/FormGeoLocationField';
import FormSymbologySelect from 'components/MapPage/CorporateViewTabs/LayersTab/FormSymbologySelect';
import {
  ActionAssigneeKind,
  DEFAULT_ACTION_PRIORITY,
  ORGANIZATION,
} from 'components/IAP/ICSConstants';
import ICSChecklist from 'components/IAP/ICSChecklist';
import {
  checklistForSubmit,
  normalizeChecklistItems,
  type ActionChecklistItem,
} from 'components/IAP/actionChecklistModel';
import type { ActionFormValues } from 'components/IAP/hooks/useScopedActionForm';
import { useActionAssigneeDirectory } from 'components/IAP/hooks/useActionAssigneeDirectory';
import { useRosterTemplatesCatalog } from 'hooks/incident/useRoster';
import { useAors } from 'hooks/useAors';
import { useActionTicketsFlag, useHwcgFlag } from 'utils/featureFlags';
import ActionFormFields from 'components/IAP/ActionFormFields';
import {
  DEFAULT_ACTION_CLASSIFICATION,
  normalizeActionClassification,
} from 'components/IAP/actionClassification';
import { ActionTicketBody } from 'components/IAP/actionTicket/ActionTicketBody';
import {
  attachmentsForSubmit,
  hazardsForSubmit,
  normalizeAttachments,
  normalizeHazards,
  normalizeRequirements,
  requirementsForSubmit,
  type ActionAttachment,
  type ActionHazard,
  type ActionResourceRequirement,
} from 'components/IAP/actionTicket/actionTicketModel';
import { dependencyOptions } from 'components/IAP/utils/actionDependencies';
import type { PlanLibraryTask } from 'hooks/planLibrary/planLibraryHooks';
import {
  emptyPlanLibraryTask,
  planTaskToActionEntity,
} from './planLibraryTaskModel';

type FormFields = {
  name: string;
  description: string;
  poc: string | null;
  briefed_poc: boolean;
  start_date: Date | string | null;
  status: string;
  priority: string;
  assignee_kind: ActionAssigneeKind;
  assignee_id: string | null;
  classification: string;
  symbology: string;
  target_date: Date | string | null;
  geolocation?: PlanLibraryTask['geolocation'];
  aor_id?: string | null;
  aor_ids?: string[];
  reporting_location?: string | null;
  requested_arrival_time?: Date | string | null;
};

type PlanActionDrawerProps = {
  task?: PlanLibraryTask | null;
  siblingTasks: PlanLibraryTask[];
  saving?: boolean;
  /** Shown on the ticket workspace chip. Defaults to the plan-library label. */
  workspaceLabel?: string;
  /**
   * Incident-only on live Tasks; plan templates still collect it. Response
   * checklist items follow the org-action Tasks tab (`false`).
   */
  showClassification?: boolean;
  /**
   * Create Event response plans assign a person or a role. Plan library and
   * incident tasks keep Person / Team / ICS Position.
   */
  assignPersonOrRole?: boolean;
  /** Event Responsible AORs, used when this task has none of its own yet. */
  defaultAorIds?: string[];
  onClose: () => void;
  onSave?: (task: Omit<PlanLibraryTask, 'id'> & { id?: string }) => void;
  /** Preview only. Save is hidden and edits are not written back. */
  readOnly?: boolean;
};

const ASSIGNEE_KIND_LABEL: Record<ActionAssigneeKind, string> = {
  user: 'Person',
  team: 'Team',
  position: 'ICS Position',
  role: 'Role',
};

function initialAssigneeKind(
  task?: PlanLibraryTask | null
): ActionAssigneeKind {
  if (
    task?.assignee_kind === 'team' ||
    task?.assignee_kind === 'position' ||
    task?.assignee_kind === 'role'
  ) {
    return task.assignee_kind;
  }
  return 'user';
}

function initialAorIds(
  task: PlanLibraryTask | null | undefined,
  defaultAorIds: string[],
  assignPersonOrRole: boolean
): string[] {
  if (task?.aor_ids?.length) return task.aor_ids;
  if (task?.aor_id) return [task.aor_id];
  return assignPersonOrRole ? defaultAorIds : [];
}

function positionTitles(
  catalog: ReturnType<typeof useRosterTemplatesCatalog>['data'],
  savedTitle?: string | null
): { label: string; value: string }[] {
  const titles = new Set<string>();
  for (const title of catalog?.addablePositionTitles ?? []) {
    const trimmed = title.trim();
    if (trimmed) titles.add(trimmed);
  }
  for (const template of catalog?.templates ?? []) {
    for (const node of template.nodes ?? []) {
      const trimmed = node.name?.trim();
      if (trimmed) titles.add(trimmed);
    }
  }
  const saved = savedTitle?.trim();
  if (saved) titles.add(saved);
  return [...titles]
    .sort((a, b) => a.localeCompare(b))
    .map((title) => ({ label: title, value: title }));
}

export function PlanActionDrawer({
  task,
  siblingTasks,
  saving = false,
  workspaceLabel = 'Plan template',
  showClassification = true,
  assignPersonOrRole = false,
  defaultAorIds = [],
  onClose,
  onSave,
  readOnly = false,
}: PlanActionDrawerProps) {
  const isEdit = Boolean(task);
  const tickets = useActionTicketsFlag();
  const hwcg = useHwcgFlag();
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: aors = [] } = useAors();
  const startingAorIds = initialAorIds(task, defaultAorIds, assignPersonOrRole);
  const rosterCatalog = useRosterTemplatesCatalog();
  const directory = useActionAssigneeDirectory(ORGANIZATION);
  const selectedEntity = task ? planTaskToActionEntity(task) : undefined;

  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: isEdit
      ? {
          name: task?.name ?? '',
          description: task?.description ?? '',
          poc: task?.poc ?? null,
          briefed_poc: task?.briefed_poc ?? false,
          start_date: task?.start_date ?? null,
          status: task?.status || 'Planned',
          priority: task?.priority || DEFAULT_ACTION_PRIORITY,
          assignee_kind: initialAssigneeKind(task),
          assignee_id: task?.assignee_id ?? null,
          classification: normalizeActionClassification(task?.classification),
          symbology: task?.symbology ?? '',
          target_date: task?.target_date ?? null,
          geolocation: task?.geolocation ?? null,
          aor_id: startingAorIds[0] ?? task?.aor_id ?? null,
          aor_ids: startingAorIds,
          reporting_location: task?.reporting_location ?? '',
          requested_arrival_time: task?.requested_arrival_time ?? null,
        }
      : {
          name: '',
          description: '',
          status: 'Planned',
          priority: DEFAULT_ACTION_PRIORITY,
          assignee_kind: 'user',
          poc: null,
          assignee_id: null,
          briefed_poc: false,
          classification: DEFAULT_ACTION_CLASSIFICATION,
          symbology: '',
          geolocation: null,
          aor_id: startingAorIds[0] ?? null,
          aor_ids: startingAorIds,
          start_date: null,
          target_date: null,
          reporting_location: '',
          requested_arrival_time: null,
        },
  });
  const { handleSubmit, watch } = form;
  const watchedName = watch('name');
  const assigneeKind = watch('assignee_kind') ?? 'user';
  const watchedAssigneeId = watch('assignee_id');

  const [checklist, setChecklist] = useState<ActionChecklistItem[]>(() =>
    normalizeChecklistItems(task?.checklist)
  );
  const [dependsOn, setDependsOn] = useState<string[]>(task?.depends_on ?? []);
  const [requirements, setRequirements] = useState<ActionResourceRequirement[]>(
    () => normalizeRequirements(task?.resource_requirements)
  );
  const [requirementsNa, setRequirementsNa] = useState(() =>
    Boolean(task?.resource_requirements_na)
  );
  const [hazards, setHazards] = useState<ActionHazard[]>(() =>
    normalizeHazards(task?.hazards)
  );
  const [attachments, setAttachments] = useState<ActionAttachment[]>(() =>
    normalizeAttachments(task?.attachments)
  );

  const teamOptions = directory.teams;
  const positionOptions = useMemo(
    () =>
      hwcg
        ? positionTitles(
            rosterCatalog.data,
            task?.assignee_kind === 'position' ? task.assignee_id : null
          )
        : [],
    [hwcg, rosterCatalog.data, task]
  );
  const assigneeKindOptions = assignPersonOrRole
    ? [
        { label: ASSIGNEE_KIND_LABEL.user, value: 'user' },
        { label: ASSIGNEE_KIND_LABEL.role, value: 'role' },
      ]
    : [
        { label: ASSIGNEE_KIND_LABEL.user, value: 'user' },
        ...(teamOptions.length
          ? [{ label: ASSIGNEE_KIND_LABEL.team, value: 'team' }]
          : []),
        ...(hwcg
          ? [{ label: ASSIGNEE_KIND_LABEL.position, value: 'position' }]
          : []),
      ];
  const { data: roleScopeMembers = [] } = useQuery({
    queryKey: ['role-scope-members', groupGuid],
    enabled: assignPersonOrRole && Boolean(groupGuid),
    queryFn: async () => {
      const response = await axios.post<RoleScopeMember[]>(
        apiConfig.apiGateway.roleScopeMembersForGroup,
        { group_guid: groupGuid }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });
  const roleOptions = useMemo(
    () => (assignPersonOrRole ? roleScopeOptions(roleScopeMembers) : []),
    [assignPersonOrRole, roleScopeMembers]
  );
  const aorOptions = aors.map((aor) => ({ label: aor.name, value: aor.id }));
  const dependencyChoices = useMemo(
    () =>
      dependencyOptions(
        task?.id,
        siblingTasks.map((sibling) => ({
          action_id: sibling.id,
          name: sibling.name,
          archived: false,
          depends_on: sibling.depends_on,
        }))
      ),
    [siblingTasks, task?.id]
  );

  const onSubmit = (formData: FormFields) => {
    const isUserAssignment = assigneeKind === 'user';
    const isPositionAssignment = assigneeKind === 'position';
    const isRoleAssignment = assigneeKind === 'role';
    const positionTitle = formData.assignee_id?.trim() || null;
    const roleKey = formData.assignee_id?.trim() || null;
    const selectedAorIds = assignPersonOrRole
      ? (formData.aor_ids ?? []).filter(Boolean)
      : formData.aor_id
        ? [formData.aor_id]
        : [];
    const values: ActionFormValues = {
      ...(formData as ActionFormValues),
      checklist: checklistForSubmit(checklist),
      depends_on: dependsOn,
      assignee_kind: isUserAssignment
        ? formData.poc
          ? 'user'
          : null
        : isPositionAssignment
          ? positionTitle
            ? 'position'
            : null
          : isRoleAssignment
            ? roleKey
              ? 'role'
              : null
            : formData.assignee_id
              ? 'team'
              : null,
      assignee_id: isUserAssignment
        ? (formData.poc ?? null)
        : isPositionAssignment
          ? positionTitle
          : isRoleAssignment
            ? roleKey
            : (formData.assignee_id ?? null),
      poc: isUserAssignment ? (formData.poc ?? null) : null,
      resource_requirements: requirementsNa
        ? []
        : requirementsForSubmit(requirements),
      resource_requirements_na: requirementsNa,
      hazards: hazardsForSubmit(hazards),
      attachments: attachmentsForSubmit(attachments),
    };
    if (readOnly || !onSave) return;
    onSave({
      ...emptyPlanLibraryTask(),
      ...values,
      id: task?.id,
      name: values.name,
      description: values.description ?? '',
      status: values.status || 'Planned',
      priority: (values.priority ||
        DEFAULT_ACTION_PRIORITY) as PlanLibraryTask['priority'],
      classification: normalizeActionClassification(values.classification),
      checklist: values.checklist,
      assignee_kind: values.assignee_kind ?? null,
      assignee_id: values.assignee_id ?? null,
      poc: values.poc ?? null,
      briefed_poc: Boolean(values.briefed_poc),
      start_date: values.start_date ?? null,
      target_date: values.target_date ?? null,
      geolocation: values.geolocation ?? null,
      symbology: values.symbology || null,
      aor_id: selectedAorIds[0] ?? null,
      aor_ids: selectedAorIds,
      reporting_location: values.reporting_location || null,
      requested_arrival_time: values.requested_arrival_time ?? null,
      resource_requirements: values.resource_requirements ?? [],
      resource_requirements_na: values.resource_requirements_na ?? false,
      hazards: values.hazards ?? [],
      attachments: values.attachments ?? [],
      depends_on: values.depends_on ?? [],
    });
  };

  const saveTask = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    void handleSubmit(onSubmit)(event);
  };

  return (
    <FormProvider {...form}>
      <FormModal
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={isEdit ? 'Edit Action' : 'Add Task'}
        widthClassName={
          tickets
            ? 'tw:!max-w-6xl tw:sm:!max-w-6xl'
            : 'tw:!max-w-2xl tw:sm:!max-w-2xl'
        }
        formProps={{ onSubmit: saveTask }}
        footer={
          <div className="tw:flex tw:flex-wrap tw:justify-end tw:gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
            {readOnly ? null : (
              <Button
                type="button"
                disabled={(!isEdit && !watchedName) || saving}
                onClick={(event) => {
                  event.stopPropagation();
                  void handleSubmit(onSubmit)();
                }}
              >
                Save
              </Button>
            )}
          </div>
        }
      >
        <fieldset
          disabled={readOnly}
          className="tw:m-0 tw:min-w-0 tw:border-0 tw:p-0"
        >
          {tickets ? (
            <ActionTicketBody
              entity={selectedEntity}
              displayId={null}
              archived={false}
              assigneeKind={assigneeKind}
              assigneeKindOptions={assigneeKindOptions}
              pocOptions={directory.users}
              teamOptions={teamOptions}
              positionOptions={positionOptions}
              roleOptions={roleOptions}
              aorOptions={aorOptions}
              aorTitle={assignPersonOrRole ? 'Responsible AOR' : undefined}
              aorMulti={assignPersonOrRole}
              showObjective={false}
              workspaceLabel={workspaceLabel}
              dependencyChoices={dependencyChoices}
              dependsOn={dependsOn}
              onDependsOnChange={readOnly ? () => undefined : setDependsOn}
              assigneeId={watchedAssigneeId}
              showClassification={showClassification}
              requirements={requirements}
              onRequirementsChange={
                readOnly ? () => undefined : setRequirements
              }
              requirementsNa={requirementsNa}
              onRequirementsNaChange={
                readOnly ? () => undefined : setRequirementsNa
              }
              hazards={hazards}
              onHazardsChange={readOnly ? () => undefined : setHazards}
              attachments={attachments}
              onAttachmentsChange={readOnly ? () => undefined : setAttachments}
              formOptions={[]}
              extraFields={
                <>
                  {hwcg ? null : (
                    <>
                      <div className="tw:space-y-1.5">
                        <FormSymbologySelect variant="shadcn" />
                        <p className="tw:m-0 tw:text-[0.675rem] tw:text-muted-foreground">
                          The symbol this task draws with on the map. Leave as
                          None to keep the status and priority marker.
                        </p>
                      </div>
                      <div className="tw:space-y-1.5">
                        <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
                          Location
                        </div>
                        <FormGeoLocationField name="geolocation" label="" />
                      </div>
                    </>
                  )}
                  <ICSChecklist
                    checklist={checklist}
                    setChecklist={readOnly ? () => undefined : setChecklist}
                    editable={!readOnly}
                    existingData={isEdit}
                    asSection
                    responsibleIndividualOptions={directory.users}
                  />
                </>
              }
            />
          ) : (
            <ActionFormFields
              isEdit={isEdit}
              selectedEntity={selectedEntity}
              displayId={null}
              workspaceLabel={workspaceLabel}
              assigneeKind={assigneeKind}
              assigneeKindOptions={assigneeKindOptions}
              aorOptions={aorOptions}
              showObjective={false}
              pocOptions={directory.users}
              teamOptions={teamOptions}
              positionOptions={positionOptions}
              roleOptions={roleOptions}
              aorTitle={assignPersonOrRole ? 'Responsible AOR' : undefined}
              aorMulti={assignPersonOrRole}
              dependencyChoices={dependencyChoices}
              dependsOn={dependsOn}
              onDependsOnChange={readOnly ? () => undefined : setDependsOn}
              assigneeId={watchedAssigneeId}
              showClassification={showClassification}
              checklist={checklist}
              setChecklist={readOnly ? () => undefined : setChecklist}
              subTaskResponsibleOptions={directory.users}
            />
          )}
        </fieldset>
      </FormModal>
    </FormProvider>
  );
}
